import { Collapse, styled } from '@mui/material';

import { ReactNode, useEffect, useState } from 'react';

import { useI18n } from '@app-velocity/i18n';

import { UnfoldLessIcon, UnfoldMoreIcon } from '@app-velocity/icons';

import { Skeleton, Typography } from '../..';

const FilterContainer = styled('div')({
  minWidth: 0,
});

const StyledFiltersContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const StyledExpandButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 12,
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: 'var(--text-text-primary-color)',
  svg: {
    fill: 'var(--text-text-primary-color)',
  },
  whiteSpace: 'nowrap',
});

const FiltersGrid = styled('div')<{ size: number }>(({ theme, size }) => ({
  display: 'grid',
  gridTemplateColumns: `${Array.from({ length: size })
    .map(() => '1fr')
    .join(' ')}`,
  gap: theme.spacing(2),
}));

export type FiltersContainerProps = {
  loading: boolean;
  filters: ReactNode[];
  moreFilters?: ReactNode[];
  moreFiltersActive?: boolean;
};

export const FiltersContainer = ({
  loading,
  filters,
  moreFilters,
  moreFiltersActive = false,
}: FiltersContainerProps) => {
  const { t } = useI18n();
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(moreFiltersActive);

  const defaultFiltersGridSize = Math.max(filters.length, 3);
  const moreFiltersGridSize = Math.max(moreFilters?.length || 0, 3);

  useEffect(() => {
    if (moreFiltersActive) {
      setIsMoreFiltersOpen(true);
    }
  }, [moreFiltersActive]);

  if (loading) {
    return (
      <StyledFiltersContainer>
        <FiltersGrid size={filters.length}>
          {Array.from({ length: defaultFiltersGridSize }).map((_, index) => (
            <FilterContainer key={index}>
              <Skeleton height={44} borderRadius={2} margin="0" />
            </FilterContainer>
          ))}
        </FiltersGrid>
        {moreFilters?.length ? (
          <>
            <Skeleton height={32} width={120} borderRadius={2} margin="0" />
            <FiltersGrid size={moreFiltersGridSize}>
              {Array.from({ length: moreFilters.length }).map((_, index) => (
                <FilterContainer key={index}>
                  <Skeleton height={44} borderRadius={2} margin="0" />
                </FilterContainer>
              ))}
            </FiltersGrid>
          </>
        ) : null}
      </StyledFiltersContainer>
    );
  }

  return (
    <StyledFiltersContainer>
      <Typography variant="subtitle1">{t('shared:filter_by')}</Typography>
      <FiltersGrid size={defaultFiltersGridSize}>
        {filters.map((filter, index) => (
          <FilterContainer key={index}>{filter}</FilterContainer>
        ))}
      </FiltersGrid>
      {moreFilters?.length ? (
        <>
          {isMoreFiltersOpen ? (
            <StyledExpandButton onClick={() => setIsMoreFiltersOpen(false)}>
              {t('shared:more_filters', { count: moreFilters.length })}
              <UnfoldLessIcon />
            </StyledExpandButton>
          ) : (
            <StyledExpandButton onClick={() => setIsMoreFiltersOpen(true)}>
              {t('shared:more_filters', { count: moreFilters.length })}
              <UnfoldMoreIcon />
            </StyledExpandButton>
          )}
          <Collapse in={isMoreFiltersOpen}>
            <FiltersGrid size={moreFiltersGridSize}>
              {moreFilters.map((filter, index) => (
                <FilterContainer key={index}>{filter}</FilterContainer>
              ))}
            </FiltersGrid>
          </Collapse>
        </>
      ) : null}
    </StyledFiltersContainer>
  );
};
